/**
 * Google Drive API Service - REPOSITORY MODULE
 *
 * Handles all repository-related data operations including:
 * - Library references (Biblioteca/referencias.json)
 * - Collections (Biblioteca/collections.json)
 * - PDF & file uploads (Biblioteca/PDFs/, Repositório/)
 * - Repository items, nucleos, notes/observations (Repositorio/)
 * - User profile (Repositorio/profile.json)
 * - Fichamento files (Biblioteca/Fichamentos/)
 * - PDF highlights (Biblioteca/Highlights/)
 * - Scratchpad notes (Scratchpad/notes.json)
 * - Naming config & file management (Config/)
 *
 * Inherits from GoogleDriveServiceProjects which provides project operations.
 * Core methods (getTokenOrRefresh, findOrCreateSubfolder, findOrCreateFolderInParent,
 * makeFileShareable, getFileShareableLink, appFolderId, ensureAppStructure) are
 * inherited from the parent chain.
 */

import { GoogleDriveServiceProjects } from './projects';
import { DRIVE_API_BASE, validateBeforeSave } from './core';
import { driveFetch, DriveConflictError } from './driveFetch';
import { etagCache } from './etagCache';
import type { Work } from '../../types/work';
import type { Collection } from '../../types/collection';
import type { RepositoryItem, NucleoDocumental, UserResearchProfile } from '../../types/repository';
import type { SavedPdfData } from '../../components/PdfReaderModal/types';
import type { ScratchpadNote } from '../../stores/useScratchpadStore';
import type { EmpiricalCodebook } from '../../types/empirical';
import { logger } from '../../utils/logger';
import { auditLog } from '../../utils/auditLog';
import { stampSchemaArray, migrateArrayIfNeeded, stampSchema, migrateIfNeeded } from './schemaMigration';

// ============================================
// SINGLE-FLIGHT — saveSinteses (Sprint 043 sub-commit 1b)
// ============================================
//
// Serializa saves concorrentes ao MESMO sinteses.json (Pilar 1 — previne
// lost-update entre o save do debounce e o save do flush: o 2º PATCH só começa
// depois que o 1º assentou). Modelo idêntico ao write queue de referencias.json
// (referencesWriteQueue.ts:24-37 — withLock). Escopo de módulo, NÃO exportado.

let _sinteseSaveLock: Promise<void> = Promise.resolve();
function withSinteseSaveLock(fn: () => Promise<void>): Promise<void> {
  const prev = _sinteseSaveLock;
  let resolve!: () => void;
  let reject!: (error: unknown) => void;
  const result = new Promise<void>((res, rej) => { resolve = res; reject = rej; });
  _sinteseSaveLock = prev.then(
    () => fn().then(resolve, reject),
    () => fn().then(resolve, reject)
  );
  _sinteseSaveLock.catch(() => {}); // Prevent unhandled rejection on chain
  return result;
}

// ============================================
// FASE EMPÍRICA v4 — HIGHLIGHTS LABEL→ID CONVERSION (DN9)
// ============================================
//
// Pure helper para converter conteúdo legacy de `empiricalTags` (que era
// labels-string em v5) para IDs do `empiricalCodebook` da Mesa (v6).
//
// Pilar 1: NÃO destrói labels que não casam com nenhuma categoria — preserva
// como string. Próxima leitura, se o codebook for atualizado (e.g. categoria
// criada manualmente pelo pesquisador depois), a conversão acontece então.

/**
 * Converte `empiricalTags` de cada highlight: labels-string → IDs do codebook.
 *
 * Comportamento:
 * - Se um label casa com `codebook.categories[].label`, substitui pelo `id`
 * - Se NÃO casa (categoria órfã ou já é ID), preserva como está + warn no logger
 * - Highlights sem `empiricalTags` ficam intocados
 * - Idempotência: rodar 2× com mesmo codebook produz mesmo resultado
 *
 * Pure, sync, retorna NOVO array (sem mutar input).
 *
 * @param highlights array de highlights tal como vem de SavedPdfData
 * @param codebook codebook empírico da Mesa (de project.json)
 * @returns highlights com `empiricalTags` convertido (ou intocado se sem codebook)
 */
export function convertHighlightLabelsToIds<T extends { id?: string; empiricalTags?: ReadonlyArray<string> | undefined }>(
  highlights: ReadonlyArray<T>,
  codebook: EmpiricalCodebook | undefined,
): T[] {
  if (!codebook || codebook.categories.length === 0) {
    // Sem codebook: fallback — preserva tudo como está
    return highlights.map((h) => ({ ...h }));
  }
  // Map label → id para lookup O(1)
  const labelToId = new Map<string, string>();
  const knownIds = new Set<string>();
  for (const cat of codebook.categories) {
    labelToId.set(cat.label, cat.id);
    knownIds.add(cat.id);
  }
  return highlights.map((h) => {
    const oldTags = h.empiricalTags;
    if (!Array.isArray(oldTags) || oldTags.length === 0) {
      return { ...h };
    }
    const newTags: string[] = [];
    for (const tag of oldTags) {
      if (knownIds.has(tag)) {
        // Já é ID — passa direto (idempotência)
        newTags.push(tag);
      } else if (labelToId.has(tag)) {
        // É label conhecido — converte para ID
        newTags.push(labelToId.get(tag)!);
      } else {
        // Órfão — preserva como string + warn (Pilar 1, Sub-ótimo aceito)
        logger.warn('GoogleDriveRepository', '[convertHighlightLabelsToIds] categoria órfã', { tag, highlightId: h.id ?? 'unknown' });
        newTags.push(tag);
      }
    }
    return { ...h, empiricalTags: newTags };
  });
}

/**
 * Aplica `convertHighlightLabelsToIds` a um `SavedPdfData` se um codebook for
 * fornecido, preservando o shape original do objeto. Usado em ambos os caminhos
 * de retorno do `loadHighlights` (normal + .bak fallback) para evitar
 * duplicação. Sprint 0.2 — wire de M1 do Sprint 0.1.
 *
 * Exported para permitir testes isolados (sem precisar mockar driveFetch +
 * tokens + folder helpers que `loadHighlights` consome internamente).
 */
export function applyCodebookConversion(
  data: SavedPdfData,
  codebook: EmpiricalCodebook | undefined,
): SavedPdfData {
  if (!codebook || !Array.isArray(data?.highlights)) {
    return data;
  }
  return {
    ...data,
    highlights: convertHighlightLabelsToIds(data.highlights, codebook),
  };
}

// Telemetria do selo de durabilidade (hotfix 2026-07): shape suspeito em
// saveHighlights = regressão de caller. Loga com SÓ metadados (Regra 19 —
// jamais conteúdo do pesquisador) e deixa o save prosseguir (decisão 1:
// não bloqueia, sem merge silencioso). Dedupe por `workId:defeito` na
// sessão para proteger a quota Sentry (padrão _backedUpFiles do core).
const SAVED_PDF_DATA_KEYS = [
  'schemaVersion',
  'highlights',
  'pageOffset',
  'ocrTextLayer',
  'lastPage',
  'annotations',
  'stickyNotes',
  'showPdfAnnotations',
] as const;
const _reportedSuspectShapes = new Set<string>();

function reportSuspectSavedPdfShape(workId: string, data: unknown): void {
  const isArray = Array.isArray(data);
  const defect = isArray ? 'top-level-array' : 'missing-highlights';
  const dedupeKey = `${workId}:${defect}`;
  if (_reportedSuspectShapes.has(dedupeKey)) return;
  _reportedSuspectShapes.add(dedupeKey);

  const presentKeys =
    isArray || data === null || typeof data !== 'object' ? [] : Object.keys(data);
  const missingKeys = SAVED_PDF_DATA_KEYS.filter((key) => !presentKeys.includes(key));
  const highlights = (data as SavedPdfData | null)?.highlights;
  const highlightCount = isArray
    ? data.length
    : Array.isArray(highlights)
      ? highlights.length
      : 0;

  logger.error(
    'GoogleDriveRepository',
    `saveHighlights recebeu shape suspeito (${defect}) — save prossegue`,
    { workId, isArray, presentKeys, missingKeys, highlightCount }
  );
}

export class GoogleDriveServiceRepository extends GoogleDriveServiceProjects {

  // ============================================
  // LIBRARY REFERENCES
  // ============================================

  /**
   * Save references array to Biblioteca/referencias.json
   */
  async saveReferencesToDrive(references: Work[]): Promise<void> {
    try {
    validateBeforeSave(references, 'saveReferencesToDrive');

    const accessToken = await this.getTokenOrRefresh();
    if (!accessToken) {
      throw new Error('No access token available');
    }

    if (!this.appFolderId) {
      throw new Error('App folder not initialized');
    }

    // Get or create Biblioteca folder
    const bibliotecaId = await this.findOrCreateSubfolder('Biblioteca');
    const fileContent = JSON.stringify(stampSchemaArray(references), null, 2);

    // Phase 2c: ID-first lookup
    const { findFileByIdOrName } = await import('./driveFileHelper');
    const { driveConfig } = await import('../driveConfig');
    const cachedFileId = driveConfig.getFileId('referenciasJson');

    const found = await findFileByIdOrName({
      cachedFileId,
      parentFolderId: bibliotecaId,
      fileName: 'referencias.json',
      accessToken,
      configFileKey: 'referenciasJson',
    });

    // Backup .bak antes de sobrescrever (once per session)
    if (found?.fileId) {
      await this.createBackupIfNeeded(found.fileId, bibliotecaId, 'referencias.json', accessToken);
    }

    if (found?.fromCache) {
      // Cached ID is canonical — PATCH directly
      const updateUrl = `https://www.googleapis.com/upload/drive/v3/files/${found.fileId}?uploadType=media`;
      const cachedEtag = etagCache.get(found.fileId);
      const updateResponse = await driveFetch(updateUrl, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          ...(cachedEtag ? { 'If-Match': cachedEtag } : {}),
        },
        body: fileContent
      });

      if (updateResponse.status === 412) {
        etagCache.delete(found.fileId);
        logger.error('GoogleDriveRepository', 'Conflito detectado em referencias.json — arquivo modificado em outro dispositivo');
        throw new DriveConflictError('referencias.json');
      }

      if (!updateResponse.ok) {
        throw new Error(`Failed to update referencias.json: ${updateResponse.statusText}`);
      }
      const newEtag = updateResponse.headers.get('ETag');
      if (newEtag) etagCache.set(found.fileId, newEtag);
      auditLog({ operation: 'save', target: 'referências', detail: `${references.length} referências salvas no Drive (cache hit)`, success: true });
      return;
    }

    if (found && !found.fromCache) {
      // Found via name search — handle duplicates
      const files = found.files || [{ id: found.fileId }];

      if (files.length > 1) {
        logger.warn('GoogleDriveRepository', `Found ${files.length} referencias.json files during save! Cleaning up...`);

        const sortedFiles = files.sort((a: { id: string; modifiedTime?: string }, b: { id: string; modifiedTime?: string }) => {
          const timeA = new Date(a.modifiedTime || 0).getTime();
          const timeB = new Date(b.modifiedTime || 0).getTime();
          return timeB - timeA;
        });

        const duplicatesToDelete = sortedFiles.slice(1);
        for (const duplicate of duplicatesToDelete) {
          try {
            await driveFetch(`${DRIVE_API_BASE}/files/${duplicate.id}`, {
              method: 'DELETE',
              headers: { 'Authorization': `Bearer ${accessToken}` }
            });
          } catch (error) {
            logger.error('GoogleDriveRepository', `Failed to delete duplicate ${duplicate.id}:`, error);
          }
        }
      }

      const fileId = files.length === 1 ? files[0].id : files.sort(
        (a: { id: string; modifiedTime?: string }, b: { id: string; modifiedTime?: string }) =>
          new Date(b.modifiedTime || 0).getTime() - new Date(a.modifiedTime || 0).getTime()
      )[0].id;

      const updateUrl = `https://www.googleapis.com/upload/drive/v3/files/${fileId}?uploadType=media`;
      const cachedEtag = etagCache.get(fileId);
      const updateResponse = await driveFetch(updateUrl, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          ...(cachedEtag ? { 'If-Match': cachedEtag } : {}),
        },
        body: fileContent
      });

      if (updateResponse.status === 412) {
        etagCache.delete(fileId);
        logger.error('GoogleDriveRepository', 'Conflito detectado em referencias.json — arquivo modificado em outro dispositivo');
        throw new DriveConflictError('referencias.json');
      }

      if (!updateResponse.ok) {
        throw new Error(`Failed to update referencias.json: ${updateResponse.statusText}`);
      }
      const newEtag = updateResponse.headers.get('ETag');
      if (newEtag) etagCache.set(fileId, newEtag);
      auditLog({ operation: 'save', target: 'referências', detail: `${references.length} referências salvas no Drive (name search)`, success: true });
      return;
    }

    // Not found anywhere — create new file
    const metadata = {
      name: 'referencias.json',
      mimeType: 'application/json',
      parents: [bibliotecaId]
    };

    const file = new Blob([fileContent], { type: 'application/json' });
    const form = new FormData();
    form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
    form.append('file', file);

    const uploadUrl = 'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart';
    const response = await driveFetch(uploadUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`
      },
      body: form
    });

    if (!response.ok) {
      throw new Error(`Failed to create referencias.json: ${response.statusText}`);
    }
    // Capture ETag + fileId from POST response for future conflict detection
    const createData = await response.json().catch(() => null);
    if (createData?.id) {
      const postEtag = createData.etag;
      if (postEtag) etagCache.set(createData.id, postEtag);
      import('../driveConfig').then(m => m.driveConfig.registerFileId('referenciasJson', createData.id)).catch(() => {});
    }
    auditLog({ operation: 'save', target: 'referências', detail: `${references.length} referências salvas no Drive`, success: true });
    } catch (error) {
      auditLog({ operation: 'save', target: 'referências', detail: 'Falha ao salvar referências no Drive', success: false, error: String(error) });
      throw error;
    }
  }

  /**
   * Load references array from Biblioteca/referencias.json
   */
  async loadReferencesFromDrive(): Promise<Work[] | null> {
    const accessToken = await this.getTokenOrRefresh();
    if (!accessToken) {
      throw new Error('No access token available');
    }

    if (!this.appFolderId) {
      throw new Error('App folder not initialized');
    }

    // Get Biblioteca folder
    const bibliotecaId = await this.findOrCreateSubfolder('Biblioteca');

    // Phase 2c: ID-first
    const { readFileByIdOrName } = await import('./driveFileHelper');
    const { driveConfig } = await import('../driveConfig');
    const cachedFileId = driveConfig.getFileId('referenciasJson');

    const result = await readFileByIdOrName({
      cachedFileId,
      parentFolderId: bibliotecaId,
      fileName: 'referencias.json',
      accessToken,
      configFileKey: 'referenciasJson',
    });

    if (!result) return null;

    try {
      const works: Work[] = JSON.parse(result.content);
      const { data } = migrateArrayIfNeeded(works, 'referencias');
      return data;
    } catch (err) {
      logger.error('GoogleDriveRepository', 'JSON.parse failed for referencias.json — trying .bak fallback:', err);
      const fallback = await this.readBakFallback<Work[]>(
        bibliotecaId, 'referencias.json', accessToken
      );
      if (fallback) {
        logger.info('GoogleDriveRepository', 'Restored referencias.json from .bak');
        return migrateArrayIfNeeded(fallback, 'referencias').data;
      }
      throw new Error('Invalid JSON in referencias.json (no .bak available)');
    }
  }

  // ============================================
  // COLLECTIONS
  // ============================================

  /**
   * Save collections array to Biblioteca/collections.json
   */
  async saveCollections(collections: Collection[]): Promise<void> {
    try {
    validateBeforeSave(collections, 'saveCollections');

    const accessToken = await this.getTokenOrRefresh();
    if (!accessToken) {
      throw new Error('No access token available');
    }

    if (!this.appFolderId) {
      throw new Error('App folder not initialized');
    }

    // Get or create Biblioteca folder
    const bibliotecaId = await this.findOrCreateSubfolder('Biblioteca');
    const fileContent = JSON.stringify(stampSchemaArray(collections), null, 2);

    // Phase 2c: ID-first lookup
    const { findFileByIdOrName } = await import('./driveFileHelper');
    const { driveConfig } = await import('../driveConfig');
    const cachedFileId = driveConfig.getFileId('collectionsJson');

    const found = await findFileByIdOrName({
      cachedFileId,
      parentFolderId: bibliotecaId,
      fileName: 'collections.json',
      accessToken,
      configFileKey: 'collectionsJson',
    });

    // Backup .bak antes de sobrescrever (once per session)
    if (found?.fileId) {
      await this.createBackupIfNeeded(found.fileId, bibliotecaId, 'collections.json', accessToken);
    }

    if (found?.fromCache) {
      // Cached ID is canonical — PATCH directly
      const updateUrl = `https://www.googleapis.com/upload/drive/v3/files/${found.fileId}?uploadType=media`;
      const cachedEtag = etagCache.get(found.fileId);
      const updateResponse = await driveFetch(updateUrl, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          ...(cachedEtag ? { 'If-Match': cachedEtag } : {}),
        },
        body: fileContent
      });

      if (updateResponse.status === 412) {
        etagCache.delete(found.fileId);
        logger.error('GoogleDriveRepository', 'Conflito detectado em collections.json — arquivo modificado em outro dispositivo');
        throw new DriveConflictError('collections.json');
      }

      if (!updateResponse.ok) {
        throw new Error(`Failed to update collections.json: ${updateResponse.statusText}`);
      }
      const newEtag = updateResponse.headers.get('ETag');
      if (newEtag) etagCache.set(found.fileId, newEtag);
      auditLog({ operation: 'save', target: 'coleções', detail: `${collections.length} coleções salvas no Drive (cache hit)`, success: true });
      return;
    }

    if (found && !found.fromCache) {
      // Found via name search — handle duplicates
      const files = found.files || [{ id: found.fileId }];

      if (files.length > 1) {
        logger.warn('GoogleDriveRepository', `Found ${files.length} collections.json files during save! Cleaning up...`);

        const sortedFiles = files.sort((a: { id: string; modifiedTime?: string }, b: { id: string; modifiedTime?: string }) => {
          const timeA = new Date(a.modifiedTime || 0).getTime();
          const timeB = new Date(b.modifiedTime || 0).getTime();
          return timeB - timeA;
        });

        const duplicatesToDelete = sortedFiles.slice(1);
        for (const duplicate of duplicatesToDelete) {
          try {
            await driveFetch(`${DRIVE_API_BASE}/files/${duplicate.id}`, {
              method: 'DELETE',
              headers: { 'Authorization': `Bearer ${accessToken}` }
            });
          } catch (error) {
            logger.error('GoogleDriveRepository', `Failed to delete duplicate ${duplicate.id}:`, error);
          }
        }
      }

      const fileId = files.length === 1 ? files[0].id : files.sort(
        (a: { id: string; modifiedTime?: string }, b: { id: string; modifiedTime?: string }) =>
          new Date(b.modifiedTime || 0).getTime() - new Date(a.modifiedTime || 0).getTime()
      )[0].id;

      const updateUrl = `https://www.googleapis.com/upload/drive/v3/files/${fileId}?uploadType=media`;
      const cachedEtag = etagCache.get(fileId);
      const updateResponse = await driveFetch(updateUrl, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          ...(cachedEtag ? { 'If-Match': cachedEtag } : {}),
        },
        body: fileContent
      });

      if (updateResponse.status === 412) {
        etagCache.delete(fileId);
        logger.error('GoogleDriveRepository', 'Conflito detectado em collections.json — arquivo modificado em outro dispositivo');
        throw new DriveConflictError('collections.json');
      }

      if (!updateResponse.ok) {
        throw new Error(`Failed to update collections.json: ${updateResponse.statusText}`);
      }
      const newEtag = updateResponse.headers.get('ETag');
      if (newEtag) etagCache.set(fileId, newEtag);
      auditLog({ operation: 'save', target: 'coleções', detail: `${collections.length} coleções salvas no Drive (name search)`, success: true });
      return;
    }

    // Not found anywhere — create new file
    const metadata = {
      name: 'collections.json',
      mimeType: 'application/json',
      parents: [bibliotecaId]
    };

    const file = new Blob([fileContent], { type: 'application/json' });
    const form = new FormData();
    form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
    form.append('file', file);

    const uploadUrl = 'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart';
    const response = await driveFetch(uploadUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`
      },
      body: form
    });

    if (!response.ok) {
      throw new Error(`Failed to create collections.json: ${response.statusText}`);
    }
    // Capture ETag + fileId from POST response for future conflict detection
    const createData = await response.json().catch(() => null);
    if (createData?.id) {
      const postEtag = createData.etag;
      if (postEtag) etagCache.set(createData.id, postEtag);
      import('../driveConfig').then(m => m.driveConfig.registerFileId('collectionsJson', createData.id)).catch(() => {});
    }
    auditLog({ operation: 'save', target: 'coleções', detail: `${collections.length} coleções salvas no Drive`, success: true });
    } catch (error) {
      auditLog({ operation: 'save', target: 'coleções', detail: 'Falha ao salvar coleções no Drive', success: false, error: String(error) });
      throw error;
    }
  }

  /**
   * Load collections array from Biblioteca/collections.json
   */
  async loadCollections(): Promise<Collection[] | null> {
    const accessToken = await this.getTokenOrRefresh();
    if (!accessToken) {
      throw new Error('No access token available');
    }

    if (!this.appFolderId) {
      throw new Error('App folder not initialized');
    }

    // Get Biblioteca folder
    const bibliotecaId = await this.findOrCreateSubfolder('Biblioteca');

    // Phase 2c: ID-first
    const { readFileByIdOrName } = await import('./driveFileHelper');
    const { driveConfig } = await import('../driveConfig');
    const cachedFileId = driveConfig.getFileId('collectionsJson');

    const result = await readFileByIdOrName({
      cachedFileId,
      parentFolderId: bibliotecaId,
      fileName: 'collections.json',
      accessToken,
      configFileKey: 'collectionsJson',
    });

    if (!result) return null;

    try {
      const collections: Collection[] = JSON.parse(result.content);
      const { data } = migrateArrayIfNeeded(collections, 'collections');
      return data;
    } catch (err) {
      logger.error('GoogleDriveRepository', 'JSON.parse failed for collections.json — trying .bak fallback:', err);
      const fallback = await this.readBakFallback<Collection[]>(
        bibliotecaId, 'collections.json', accessToken
      );
      if (fallback) {
        logger.info('GoogleDriveRepository', 'Restored collections.json from .bak');
        return migrateArrayIfNeeded(fallback, 'collections').data;
      }
      throw new Error('Invalid JSON in collections.json (no .bak available)');
    }
  }

  // ============================================
  // PDF & FILE UPLOADS
  // ============================================

  /**
   * Upload PDF file to Google Drive (Biblioteca/PDFs/ folder)
   * Returns file ID and shareable URL
   * FIXED: Uses findOrCreateFolderInParent to prevent duplicate PDFs folders
   */
  async uploadReferencePdf(
    file: File,
    fileName: string
  ): Promise<{ fileId: string; fileUrl: string; fileName: string }> {
    const accessToken = await this.getTokenOrRefresh();
    if (!accessToken) {
      throw new Error('No access token available');
    }

    if (!this.appFolderId) {
      throw new Error('App folder not initialized');
    }

    // Get or create Biblioteca/PDFs folder - FIXED: prevents duplicates
    const bibliotecaId = await this.findOrCreateSubfolder('Biblioteca');
    const pdfsId = await this.findOrCreateFolderInParent(bibliotecaId, 'PDFs');

    // Upload file using multipart upload
    const metadata = {
      name: fileName,
      mimeType: 'application/pdf',
      parents: [pdfsId]
    };

    const form = new FormData();
    form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
    form.append('file', file);

    const uploadUrl = 'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,webViewLink';
    const response = await driveFetch(uploadUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`
      },
      body: form
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(`Failed to upload PDF: ${error.error?.message || response.statusText}`);
    }

    const data = await response.json();

    // Make file shareable (anyone with link can view)
    await this.makeFileShareable(data.id);

    // Get shareable link
    const shareableUrl = await this.getFileShareableLink(data.id);

    return {
      fileId: data.id,
      fileUrl: shareableUrl,
      fileName: fileName
    };
  }

  /**
   * Upload a file to the Repository folder
   * FIXED: Uses findOrCreateFolderInParent to prevent duplicate dataType folders
   */
  async uploadRepositoryFile(
    file: File,
    fileName: string,
    dataType?: string
  ): Promise<{ fileId: string; fileUrl: string; fileName: string }> {
    const accessToken = await this.getTokenOrRefresh();
    if (!accessToken) {
      throw new Error('No access token available');
    }

    if (!this.appFolderId) {
      throw new Error('App folder not initialized');
    }

    // Get or create Repositório folder
    const repositorioId = await this.findOrCreateSubfolder('Repositorio');

    // Optionally create subfolder by data type - FIXED: prevents duplicates
    let targetFolderId = repositorioId;
    if (dataType) {
      targetFolderId = await this.findOrCreateFolderInParent(repositorioId, dataType);
    }

    // Upload file using multipart upload
    const metadata = {
      name: fileName,
      mimeType: file.type || 'application/octet-stream',
      parents: [targetFolderId]
    };

    const form = new FormData();
    form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
    form.append('file', file);

    const uploadUrl = 'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,webViewLink';
    const response = await driveFetch(uploadUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`
      },
      body: form
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(`Failed to upload file: ${error.error?.message || response.statusText}`);
    }

    const data = await response.json();

    // Make file shareable (anyone with link can view)
    await this.makeFileShareable(data.id);

    // Get shareable link
    const shareableUrl = await this.getFileShareableLink(data.id);

    return {
      fileId: data.id,
      fileUrl: shareableUrl,
      fileName: fileName
    };
  }

  // ============================================
  // REPOSITORY ITEMS
  // ============================================

  /**
   * Save repository items to Repositorio/items.json
   */
  async saveRepositoryItems(items: RepositoryItem[]): Promise<void> {
    try {
    const accessToken = await this.getTokenOrRefresh();
    if (!accessToken) {
      throw new Error('No access token available');
    }

    if (!this.appFolderId) {
      throw new Error('App folder not initialized');
    }

    const repositorioId = await this.findOrCreateSubfolder('Repositorio');
    const query = `name='items.json' and '${repositorioId}' in parents and trashed=false`;
    const searchUrl = `${DRIVE_API_BASE}/files?q=${encodeURIComponent(query)}&fields=files(id,modifiedTime)`;

    const searchResponse = await driveFetch(searchUrl, {
      headers: { 'Authorization': `Bearer ${accessToken}` }
    });

    if (!searchResponse.ok) {
      throw new Error(`Failed to search for items.json: ${searchResponse.statusText}`);
    }

    const searchData = await searchResponse.json();
    const stamped = stampSchemaArray(items);
    validateBeforeSave(stamped, 'saveRepositoryItems');
    const fileContent = JSON.stringify(stamped, null, 2);

    if (searchData.files && searchData.files.length > 0) {
      // Handle duplicates
      if (searchData.files.length > 1) {
        const sortedFiles = searchData.files.sort((a: { id: string; modifiedTime?: string }, b: { id: string; modifiedTime?: string }) =>
          new Date(b.modifiedTime || 0).getTime() - new Date(a.modifiedTime || 0).getTime()
        );
        for (const dup of sortedFiles.slice(1)) {
          try {
            await driveFetch(`${DRIVE_API_BASE}/files/${dup.id}`, {
              method: 'DELETE',
              headers: { 'Authorization': `Bearer ${accessToken}` }
            });
          } catch (error) {
            logger.warn('GoogleDriveRepository', 'Failed to delete duplicate during cleanup', error);
          }
        }
      }

      const fileId = searchData.files[0].id;
      // Backup .bak antes de sobrescrever (once per session)
      await this.createBackupIfNeeded(fileId, repositorioId, 'items.json', accessToken);
      const updateUrl = `https://www.googleapis.com/upload/drive/v3/files/${fileId}?uploadType=media`;
      await driveFetch(updateUrl, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: fileContent
      });
    } else {
      const metadata = {
        name: 'items.json',
        mimeType: 'application/json',
        parents: [repositorioId]
      };
      const form = new FormData();
      form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
      form.append('file', new Blob([fileContent], { type: 'application/json' }));

      await driveFetch('https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${accessToken}` },
        body: form
      });
    }
    auditLog({ operation: 'save', target: 'itens repositório', detail: `${items.length} itens salvos no Drive`, success: true });
    } catch (error) {
      auditLog({ operation: 'save', target: 'itens repositório', detail: 'Falha ao salvar itens do repositório no Drive', success: false, error: String(error) });
      throw error;
    }
  }

  /**
   * OTIMIZADO: Carrega todos os arquivos JSON do Repositório em uma única busca
   * Retorna um objeto com items, nucleos, profile e observations
   */
  async loadAllRepositoryData(): Promise<{
    items: RepositoryItem[];
    nucleos: NucleoDocumental[];
    profile: UserResearchProfile | null;
    observations: Record<string, unknown>;
  }> {
    const accessToken = await this.getTokenOrRefresh();
    if (!accessToken) {
      throw new Error('No access token available');
    }

    if (!this.appFolderId) {
      throw new Error('App folder not initialized');
    }

    const repositorioId = await this.findOrCreateSubfolder('Repositorio');

    // Buscar TODOS os arquivos JSON da pasta de uma vez
    const query = `'${repositorioId}' in parents and mimeType='application/json' and trashed=false`;
    const searchUrl = `${DRIVE_API_BASE}/files?q=${encodeURIComponent(query)}&fields=files(id,name,modifiedTime)`;

    const searchResponse = await driveFetch(searchUrl, {
      headers: { 'Authorization': `Bearer ${accessToken}` }
    });

    if (!searchResponse.ok) {
      throw new Error(`Failed to search repository files: ${searchResponse.statusText}`);
    }

    const searchData = await searchResponse.json();
    const files = searchData.files || [];

    // Mapear arquivos por nome (pegando o mais recente em caso de duplicatas)
    const fileMap = new Map<string, string>();
    const filesByName: Record<string, Array<{ id: string; name: string; modifiedTime?: string }>> = {};

    for (const file of files) {
      if (!filesByName[file.name]) {
        filesByName[file.name] = [];
      }
      filesByName[file.name].push(file);
    }

    // Pegar o mais recente de cada arquivo
    for (const [name, fileList] of Object.entries(filesByName)) {
      if (fileList.length > 1) {
        fileList.sort((a: { id: string; modifiedTime?: string }, b: { id: string; modifiedTime?: string }) =>
          new Date(b.modifiedTime || 0).getTime() - new Date(a.modifiedTime || 0).getTime()
        );
      }
      fileMap.set(name, fileList[0].id);
    }

    // Baixar todos os arquivos em PARALELO
    const downloadPromises: Promise<{ name: string; data: unknown }>[] = [];

    for (const [name, fileId] of fileMap.entries()) {
      downloadPromises.push(
        driveFetch(`${DRIVE_API_BASE}/files/${fileId}?alt=media`, {
          headers: { 'Authorization': `Bearer ${accessToken}` }
        })
        .then(res => res.ok ? res.text() : Promise.reject(new Error(`Failed to download ${name}`)))
        .then(text => ({ name, data: JSON.parse(text) }))
        .catch(err => {
          logger.error('GoogleDriveRepository', `Failed to load ${name}:`, err);
          return { name, data: null };
        })
      );
    }

    const results = await Promise.all(downloadPromises);

    // Organizar resultados
    const result = {
      items: [] as RepositoryItem[],
      nucleos: [] as NucleoDocumental[],
      profile: null as UserResearchProfile | null,
      observations: {} as Record<string, unknown>
    };

    for (const { name, data } of results) {
      if (!data) continue;

      switch (name) {
        case 'items.json':
          result.items = migrateArrayIfNeeded(data as RepositoryItem[], 'repositoryItems').data;
          break;
        case 'nucleos.json':
          result.nucleos = migrateArrayIfNeeded(data as NucleoDocumental[], 'repositoryNucleos').data;
          break;
        case 'profile.json':
          result.profile = migrateIfNeeded(data as UserResearchProfile, 'userProfile').data;
          break;
        case 'notes.json':
        case 'observations.json':
          result.observations = migrateIfNeeded(data as Record<string, unknown>, 'repositoryNotes').data;
          break;
      }
    }

    return result;
  }

  /**
   * Load repository items from Repositorio/items.json
   */
  async loadRepositoryItems(): Promise<RepositoryItem[] | null> {
    const accessToken = await this.getTokenOrRefresh();
    if (!accessToken) {
      throw new Error('No access token available');
    }

    if (!this.appFolderId) {
      throw new Error('App folder not initialized');
    }

    const repositorioId = await this.findOrCreateSubfolder('Repositorio');
    const query = `name='items.json' and '${repositorioId}' in parents and trashed=false`;
    const searchUrl = `${DRIVE_API_BASE}/files?q=${encodeURIComponent(query)}&fields=files(id,modifiedTime)`;

    const searchResponse = await driveFetch(searchUrl, {
      headers: { 'Authorization': `Bearer ${accessToken}` }
    });

    if (!searchResponse.ok) {
      throw new Error(`Failed to search for items.json: ${searchResponse.statusText}`);
    }

    const searchData = await searchResponse.json();

    if (!searchData.files || searchData.files.length === 0) {
      return null;
    }

    // Handle duplicates - use most recent
    let fileId = searchData.files[0].id;
    if (searchData.files.length > 1) {
      const sortedFiles = searchData.files.sort((a: { id: string; modifiedTime?: string }, b: { id: string; modifiedTime?: string }) =>
        new Date(b.modifiedTime || 0).getTime() - new Date(a.modifiedTime || 0).getTime()
      );
      fileId = sortedFiles[0].id;
    }

    const downloadUrl = `${DRIVE_API_BASE}/files/${fileId}?alt=media`;
    const downloadResponse = await driveFetch(downloadUrl, {
      headers: { 'Authorization': `Bearer ${accessToken}` }
    });

    if (!downloadResponse.ok) {
      throw new Error(`Failed to download items.json: ${downloadResponse.statusText}`);
    }

    const jsonText = await downloadResponse.text();
    try {
      const items: RepositoryItem[] = JSON.parse(jsonText);
      return migrateArrayIfNeeded(items, 'repositoryItems').data;
    } catch (err) {
      logger.error('GoogleDriveRepository', 'JSON.parse failed for items.json — trying .bak fallback:', err);
      const fallback = await this.readBakFallback<RepositoryItem[]>(
        repositorioId, 'items.json', accessToken
      );
      if (fallback) {
        logger.info('GoogleDriveRepository', 'Restored items.json from .bak');
        return migrateArrayIfNeeded(fallback, 'repositoryItems').data;
      }
      throw new Error('Invalid JSON in items.json (no .bak available)');
    }
  }

  // ============================================
  // REPOSITORY NUCLEOS
  // ============================================

  /**
   * Save repository nucleos to Repositorio/nucleos.json
   */
  async saveRepositoryNucleos(nucleos: NucleoDocumental[]): Promise<void> {
    try {
    const accessToken = await this.getTokenOrRefresh();
    if (!accessToken) {
      throw new Error('No access token available');
    }

    if (!this.appFolderId) {
      throw new Error('App folder not initialized');
    }

    const repositorioId = await this.findOrCreateSubfolder('Repositorio');
    const query = `name='nucleos.json' and '${repositorioId}' in parents and trashed=false`;
    const searchUrl = `${DRIVE_API_BASE}/files?q=${encodeURIComponent(query)}&fields=files(id,modifiedTime)`;

    const searchResponse = await driveFetch(searchUrl, {
      headers: { 'Authorization': `Bearer ${accessToken}` }
    });

    if (!searchResponse.ok) {
      throw new Error(`Failed to search for nucleos.json: ${searchResponse.statusText}`);
    }

    const searchData = await searchResponse.json();
    const stamped = stampSchemaArray(nucleos);
    validateBeforeSave(stamped, 'saveRepositoryNucleos');
    const fileContent = JSON.stringify(stamped, null, 2);

    if (searchData.files && searchData.files.length > 0) {
      if (searchData.files.length > 1) {
        const sortedFiles = searchData.files.sort((a: { id: string; modifiedTime?: string }, b: { id: string; modifiedTime?: string }) =>
          new Date(b.modifiedTime || 0).getTime() - new Date(a.modifiedTime || 0).getTime()
        );
        for (const dup of sortedFiles.slice(1)) {
          try {
            await driveFetch(`${DRIVE_API_BASE}/files/${dup.id}`, {
              method: 'DELETE',
              headers: { 'Authorization': `Bearer ${accessToken}` }
            });
          } catch (error) {
            logger.warn('GoogleDriveRepository', 'Failed to delete duplicate during cleanup', error);
          }
        }
      }

      const fileId = searchData.files[0].id;
      // Backup .bak antes de sobrescrever (once per session)
      await this.createBackupIfNeeded(fileId, repositorioId, 'nucleos.json', accessToken);
      await driveFetch(`https://www.googleapis.com/upload/drive/v3/files/${fileId}?uploadType=media`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: fileContent
      });
    } else {
      const metadata = {
        name: 'nucleos.json',
        mimeType: 'application/json',
        parents: [repositorioId]
      };
      const form = new FormData();
      form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
      form.append('file', new Blob([fileContent], { type: 'application/json' }));

      await driveFetch('https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${accessToken}` },
        body: form
      });
    }
    auditLog({ operation: 'save', target: 'núcleos', detail: `${nucleos.length} núcleos salvos no Drive`, success: true });
    } catch (error) {
      auditLog({ operation: 'save', target: 'núcleos', detail: 'Falha ao salvar núcleos no Drive', success: false, error: String(error) });
      throw error;
    }
  }

  /**
   * Load repository nucleos from Repositorio/nucleos.json
   */
  async loadRepositoryNucleos(): Promise<NucleoDocumental[] | null> {
    const accessToken = await this.getTokenOrRefresh();
    if (!accessToken) {
      throw new Error('No access token available');
    }

    if (!this.appFolderId) {
      throw new Error('App folder not initialized');
    }

    const repositorioId = await this.findOrCreateSubfolder('Repositorio');
    const query = `name='nucleos.json' and '${repositorioId}' in parents and trashed=false`;
    const searchUrl = `${DRIVE_API_BASE}/files?q=${encodeURIComponent(query)}&fields=files(id,modifiedTime)`;

    const searchResponse = await driveFetch(searchUrl, {
      headers: { 'Authorization': `Bearer ${accessToken}` }
    });

    if (!searchResponse.ok) {
      throw new Error(`Failed to search for nucleos.json: ${searchResponse.statusText}`);
    }

    const searchData = await searchResponse.json();

    if (!searchData.files || searchData.files.length === 0) {
      return null;
    }

    let fileId = searchData.files[0].id;
    if (searchData.files.length > 1) {
      const sortedFiles = searchData.files.sort((a: { id: string; modifiedTime?: string }, b: { id: string; modifiedTime?: string }) =>
        new Date(b.modifiedTime || 0).getTime() - new Date(a.modifiedTime || 0).getTime()
      );
      fileId = sortedFiles[0].id;
    }

    const downloadResponse = await driveFetch(`${DRIVE_API_BASE}/files/${fileId}?alt=media`, {
      headers: { 'Authorization': `Bearer ${accessToken}` }
    });

    if (!downloadResponse.ok) {
      throw new Error(`Failed to download nucleos.json: ${downloadResponse.statusText}`);
    }

    const jsonText = await downloadResponse.text();
    try {
      const nucleos: NucleoDocumental[] = JSON.parse(jsonText);
      return migrateArrayIfNeeded(nucleos, 'repositoryNucleos').data;
    } catch (err) {
      logger.error('GoogleDriveRepository', 'JSON.parse failed for nucleos.json — trying .bak fallback:', err);
      const fallback = await this.readBakFallback<NucleoDocumental[]>(
        repositorioId, 'nucleos.json', accessToken
      );
      if (fallback) {
        logger.info('GoogleDriveRepository', 'Restored nucleos.json from .bak');
        return migrateArrayIfNeeded(fallback, 'repositoryNucleos').data;
      }
      throw new Error('Invalid JSON in nucleos.json (no .bak available)');
    }
  }

  // ============================================
  // REPOSITORY NOTES / OBSERVATIONS
  // ============================================

  /**
   * Save repository observations to Repositorio/observations.json
   */
  async saveRepositoryNotes(observations: Record<string, unknown>): Promise<void> {
    try {
    const accessToken = await this.getTokenOrRefresh();
    if (!accessToken) {
      throw new Error('No access token available');
    }

    if (!this.appFolderId) {
      throw new Error('App folder not initialized');
    }

    const repositorioId = await this.findOrCreateSubfolder('Repositorio');
    const query = `name='notes.json' and '${repositorioId}' in parents and trashed=false`;
    const searchUrl = `${DRIVE_API_BASE}/files?q=${encodeURIComponent(query)}&fields=files(id,modifiedTime)`;

    const searchResponse = await driveFetch(searchUrl, {
      headers: { 'Authorization': `Bearer ${accessToken}` }
    });

    if (!searchResponse.ok) {
      throw new Error(`Failed to search for notes.json: ${searchResponse.statusText}`);
    }

    const searchData = await searchResponse.json();
    const stamped = stampSchema(observations);
    validateBeforeSave(stamped, 'saveRepositoryNotes');
    const fileContent = JSON.stringify(stamped, null, 2);

    if (searchData.files && searchData.files.length > 0) {
      if (searchData.files.length > 1) {
        const sortedFiles = searchData.files.sort((a: { id: string; modifiedTime?: string }, b: { id: string; modifiedTime?: string }) =>
          new Date(b.modifiedTime || 0).getTime() - new Date(a.modifiedTime || 0).getTime()
        );
        for (const dup of sortedFiles.slice(1)) {
          try {
            await driveFetch(`${DRIVE_API_BASE}/files/${dup.id}`, {
              method: 'DELETE',
              headers: { 'Authorization': `Bearer ${accessToken}` }
            });
          } catch (error) {
            logger.warn('GoogleDriveRepository', 'Failed to delete duplicate during cleanup', error);
          }
        }
      }

      const fileId = searchData.files[0].id;
      // Backup .bak antes de sobrescrever (once per session)
      await this.createBackupIfNeeded(fileId, repositorioId, 'notes.json', accessToken);
      await driveFetch(`https://www.googleapis.com/upload/drive/v3/files/${fileId}?uploadType=media`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: fileContent
      });
    } else {
      const metadata = {
        name: 'notes.json',
        mimeType: 'application/json',
        parents: [repositorioId]
      };
      const form = new FormData();
      form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
      form.append('file', new Blob([fileContent], { type: 'application/json' }));

      await driveFetch('https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${accessToken}` },
        body: form
      });
    }
    auditLog({ operation: 'save', target: 'notas repositório', detail: 'Notas/observações salvas no Drive', success: true });
    } catch (error) {
      auditLog({ operation: 'save', target: 'notas repositório', detail: 'Falha ao salvar notas/observações no Drive', success: false, error: String(error) });
      throw error;
    }
  }

  /**
   * Load repository observations from Repositorio/observations.json (or notes.json for backwards compatibility)
   */
  async loadRepositoryNotes(): Promise<Record<string, unknown>> {
    const accessToken = await this.getTokenOrRefresh();
    if (!accessToken) {
      throw new Error('No access token available');
    }

    if (!this.appFolderId) {
      throw new Error('App folder not initialized');
    }

    const repositorioId = await this.findOrCreateSubfolder('Repositorio');
    const query = `name='notes.json' and '${repositorioId}' in parents and trashed=false`;
    const searchUrl = `${DRIVE_API_BASE}/files?q=${encodeURIComponent(query)}&fields=files(id,modifiedTime)`;

    const searchResponse = await driveFetch(searchUrl, {
      headers: { 'Authorization': `Bearer ${accessToken}` }
    });

    if (!searchResponse.ok) {
      throw new Error(`Failed to search for notes.json: ${searchResponse.statusText}`);
    }

    const searchData = await searchResponse.json();

    if (!searchData.files || searchData.files.length === 0) {
      return {};
    }

    let fileId = searchData.files[0].id;
    if (searchData.files.length > 1) {
      const sortedFiles = searchData.files.sort((a: { id: string; modifiedTime?: string }, b: { id: string; modifiedTime?: string }) =>
        new Date(b.modifiedTime || 0).getTime() - new Date(a.modifiedTime || 0).getTime()
      );
      fileId = sortedFiles[0].id;
    }

    const downloadResponse = await driveFetch(`${DRIVE_API_BASE}/files/${fileId}?alt=media`, {
      headers: { 'Authorization': `Bearer ${accessToken}` }
    });

    if (!downloadResponse.ok) {
      throw new Error(`Failed to download notes.json: ${downloadResponse.statusText}`);
    }

    const jsonText = await downloadResponse.text();
    try {
      const notes = JSON.parse(jsonText) as Record<string, unknown>;
      return migrateIfNeeded(notes, 'repositoryNotes').data;
    } catch (err) {
      logger.error('GoogleDriveRepository', 'JSON.parse failed for notes.json — trying .bak fallback:', err);
      const fallback = await this.readBakFallback<Record<string, unknown>>(
        repositorioId, 'notes.json', accessToken
      );
      if (fallback) {
        logger.info('GoogleDriveRepository', 'Restored notes.json from .bak');
        return migrateIfNeeded(fallback, 'repositoryNotes').data;
      }
      throw new Error('Invalid JSON in notes.json (no .bak available)');
    }
  }

  // ============================================
  // USER PROFILE
  // ============================================

  /**
   * Save user profile to Repositorio/profile.json
   */
  async saveUserProfile(profile: UserResearchProfile): Promise<void> {
    try {
    const accessToken = await this.getTokenOrRefresh();
    if (!accessToken) {
      throw new Error('No access token available');
    }

    if (!this.appFolderId) {
      throw new Error('App folder not initialized');
    }

    const repositorioId = await this.findOrCreateSubfolder('Repositorio');
    const query = `name='profile.json' and '${repositorioId}' in parents and trashed=false`;
    const searchUrl = `${DRIVE_API_BASE}/files?q=${encodeURIComponent(query)}&fields=files(id,modifiedTime)`;

    const searchResponse = await driveFetch(searchUrl, {
      headers: { 'Authorization': `Bearer ${accessToken}` }
    });

    if (!searchResponse.ok) {
      throw new Error(`Failed to search for profile.json: ${searchResponse.statusText}`);
    }

    const searchData = await searchResponse.json();
    const stamped = stampSchema(profile);
    validateBeforeSave(stamped, 'saveUserProfile');
    const fileContent = JSON.stringify(stamped, null, 2);

    if (searchData.files && searchData.files.length > 0) {
      const fileId = searchData.files[0].id;
      await this.createBackupIfNeeded(fileId, repositorioId, 'profile.json', accessToken);
      await driveFetch(`https://www.googleapis.com/upload/drive/v3/files/${fileId}?uploadType=media`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: fileContent
      });
    } else {
      const metadata = {
        name: 'profile.json',
        mimeType: 'application/json',
        parents: [repositorioId]
      };
      const form = new FormData();
      form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
      form.append('file', new Blob([fileContent], { type: 'application/json' }));

      await driveFetch('https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${accessToken}` },
        body: form
      });
    }
    auditLog({ operation: 'save', target: 'perfil pesquisador', detail: 'Perfil do pesquisador salvo no Drive', success: true });
    } catch (error) {
      auditLog({ operation: 'save', target: 'perfil pesquisador', detail: 'Falha ao salvar perfil do pesquisador no Drive', success: false, error: String(error) });
      throw error;
    }
  }

  /**
   * Load user profile from Repositorio/profile.json
   */
  async loadUserProfile(): Promise<UserResearchProfile | null> {
    const accessToken = await this.getTokenOrRefresh();
    if (!accessToken) {
      throw new Error('No access token available');
    }

    if (!this.appFolderId) {
      throw new Error('App folder not initialized');
    }

    const repositorioId = await this.findOrCreateSubfolder('Repositorio');
    const query = `name='profile.json' and '${repositorioId}' in parents and trashed=false`;
    const searchUrl = `${DRIVE_API_BASE}/files?q=${encodeURIComponent(query)}&fields=files(id)`;

    const searchResponse = await driveFetch(searchUrl, {
      headers: { 'Authorization': `Bearer ${accessToken}` }
    });

    if (!searchResponse.ok) {
      throw new Error(`Failed to search for profile.json: ${searchResponse.statusText}`);
    }

    const searchData = await searchResponse.json();

    if (!searchData.files || searchData.files.length === 0) {
      return null;
    }

    const fileId = searchData.files[0].id;
    const downloadResponse = await driveFetch(`${DRIVE_API_BASE}/files/${fileId}?alt=media`, {
      headers: { 'Authorization': `Bearer ${accessToken}` }
    });

    if (!downloadResponse.ok) {
      throw new Error(`Failed to download profile.json: ${downloadResponse.statusText}`);
    }

    const jsonText = await downloadResponse.text();
    try {
      const profile: UserResearchProfile = JSON.parse(jsonText);
      return migrateIfNeeded(profile, 'userProfile').data;
    } catch (err) {
      logger.error('GoogleDriveRepository', 'JSON.parse failed for profile.json — trying .bak fallback:', err);
      const fallback = await this.readBakFallback<UserResearchProfile>(
        repositorioId, 'profile.json', accessToken
      );
      if (fallback) {
        logger.info('GoogleDriveRepository', 'Restored profile.json from .bak');
        return migrateIfNeeded(fallback, 'userProfile').data;
      }
      throw new Error('Invalid JSON in profile.json (no .bak available)');
    }
  }

  // ============================================
  // USER PREFERENCES (cross-device sync)
  // ============================================

  /**
   * Save user preferences to Repositorio/preferences.json
   */
  async saveUserPreferences(preferences: Record<string, unknown>): Promise<void> {
    try {
      const accessToken = await this.getTokenOrRefresh();
      if (!accessToken) {
        throw new Error('No access token available');
      }

      if (!this.appFolderId) {
        throw new Error('App folder not initialized');
      }

      const repositorioId = await this.findOrCreateSubfolder('Repositorio');
      const query = `name='preferences.json' and '${repositorioId}' in parents and trashed=false`;
      const searchUrl = `${DRIVE_API_BASE}/files?q=${encodeURIComponent(query)}&fields=files(id,modifiedTime)`;

      const searchResponse = await driveFetch(searchUrl, {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });

      if (!searchResponse.ok) {
        throw new Error(`Failed to search for preferences.json: ${searchResponse.statusText}`);
      }

      const searchData = await searchResponse.json();
      const stamped = stampSchema(preferences);
      validateBeforeSave(stamped, 'saveUserPreferences');
      const fileContent = JSON.stringify(stamped, null, 2);

      if (searchData.files && searchData.files.length > 0) {
        const fileId = searchData.files[0].id;
        await driveFetch(`https://www.googleapis.com/upload/drive/v3/files/${fileId}?uploadType=media`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          },
          body: fileContent
        });
      } else {
        const metadata = {
          name: 'preferences.json',
          mimeType: 'application/json',
          parents: [repositorioId]
        };
        const form = new FormData();
        form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
        form.append('file', new Blob([fileContent], { type: 'application/json' }));

        await driveFetch('https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart', {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${accessToken}` },
          body: form
        });
      }
      auditLog({ operation: 'save', target: 'preferências', detail: 'Preferências salvas no Drive', success: true });
    } catch (error) {
      auditLog({ operation: 'save', target: 'preferências', detail: 'Falha ao salvar preferências no Drive', success: false, error: String(error) });
      throw error;
    }
  }

  /**
   * Load user preferences from Repositorio/preferences.json
   */
  async loadUserPreferences(): Promise<Record<string, unknown> | null> {
    const accessToken = await this.getTokenOrRefresh();
    if (!accessToken) {
      throw new Error('No access token available');
    }

    if (!this.appFolderId) {
      throw new Error('App folder not initialized');
    }

    const repositorioId = await this.findOrCreateSubfolder('Repositorio');
    const query = `name='preferences.json' and '${repositorioId}' in parents and trashed=false`;
    const searchUrl = `${DRIVE_API_BASE}/files?q=${encodeURIComponent(query)}&fields=files(id)`;

    const searchResponse = await driveFetch(searchUrl, {
      headers: { 'Authorization': `Bearer ${accessToken}` }
    });

    if (!searchResponse.ok) {
      throw new Error(`Failed to search for preferences.json: ${searchResponse.statusText}`);
    }

    const searchData = await searchResponse.json();

    if (!searchData.files || searchData.files.length === 0) {
      return null;
    }

    const fileId = searchData.files[0].id;
    const downloadResponse = await driveFetch(`${DRIVE_API_BASE}/files/${fileId}?alt=media`, {
      headers: { 'Authorization': `Bearer ${accessToken}` }
    });

    if (!downloadResponse.ok) {
      throw new Error(`Failed to download preferences.json: ${downloadResponse.statusText}`);
    }

    const jsonText = await downloadResponse.text();
    try {
      const prefs = JSON.parse(jsonText) as Record<string, unknown>;
      return migrateIfNeeded(prefs, 'preferences').data;
    } catch (err) {
      logger.error('GoogleDriveRepository', 'JSON.parse failed for preferences.json:', err);
      return null;
    }
  }

  // ============================================
  // FICHAMENTOS - Upload de documentos anexados
  // ============================================

  /**
   * Upload a fichamento document to Google Drive
   * Saves to Bigwa_App/Biblioteca/Fichamentos/{workId}/
   * FIXED: Uses findOrCreateFolderInParent to prevent duplicate folders
   *
   * @param workId - ID da obra para organizar os fichamentos
   * @param file - Arquivo a ser enviado
   * @param workTitle - Título da obra (para nomear a pasta)
   * @returns Informações do arquivo uploaded
   */
  async uploadFichamentoFile(
    workId: string,
    file: File,
    workTitle?: string
  ): Promise<{ id: string; name: string; mimeType: string; webViewLink: string }> {
    const accessToken = await this.getTokenOrRefresh();
    if (!accessToken) {
      throw new Error('No access token available');
    }

    if (!this.appFolderId) {
      throw new Error('App folder not initialized');
    }

    // Get or create Biblioteca folder
    const bibliotecaId = await this.findOrCreateSubfolder('Biblioteca');

    // Get or create Fichamentos folder inside Biblioteca - FIXED: prevents duplicates
    const fichamentosId = await this.findOrCreateFolderInParent(bibliotecaId, 'Fichamentos');

    // Create or find folder for this specific work
    const workFolderName = workTitle
      ? `${workTitle.substring(0, 50).replace(/[/\\?%*:|"<>]/g, '_')}_${workId.substring(0, 8)}`
      : workId;

    // FIXED: Use findOrCreateFolderInParent to prevent duplicate work folders
    const workFolderId = await this.findOrCreateFolderInParent(fichamentosId, workFolderName);

    // Upload file
    const metadata = {
      name: file.name,
      mimeType: file.type || 'application/octet-stream',
      parents: [workFolderId]
    };

    const form = new FormData();
    form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
    form.append('file', file);

    const uploadUrl = 'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id,name,mimeType,webViewLink';
    const response = await driveFetch(uploadUrl, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${accessToken}` },
      body: form
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(`Failed to upload fichamento file: ${error.error?.message || response.statusText}`);
    }

    const data = await response.json();

    // Make file shareable
    await this.makeFileShareable(data.id);

    // Get shareable link
    const shareableUrl = await this.getFileShareableLink(data.id);

    return {
      id: data.id,
      name: data.name,
      mimeType: data.mimeType,
      webViewLink: shareableUrl
    };
  }

  /**
   * Delete a fichamento file from Google Drive
   * @param fileId - ID do arquivo no Google Drive
   */
  async deleteFichamentoFile(fileId: string): Promise<void> {
    const accessToken = await this.getTokenOrRefresh();
    if (!accessToken) {
      throw new Error('No access token available');
    }

    const deleteUrl = `${DRIVE_API_BASE}/files/${fileId}`;
    const response = await driveFetch(deleteUrl, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${accessToken}` }
    });

    if (!response.ok && response.status !== 404) {
      throw new Error(`Failed to delete fichamento file: ${response.statusText}`);
    }
  }

  /**
   * Open a fichamento file in a new tab
   * @param webViewLink - Link do arquivo no Google Drive
   */
  openFichamentoFile(webViewLink: string): void {
    window.open(webViewLink, '_blank');
  }

  // ============================================
  // HIGHLIGHTS DE PDF
  // ============================================

  /**
   * Salva highlights de um PDF
   * Armazena em Biblioteca/Highlights/{workId}.json
   */
  async saveHighlights(workId: string, data: SavedPdfData): Promise<void> {
    try {
    if (Array.isArray(data) || !data?.highlights) {
      reportSuspectSavedPdfShape(workId, data);
    }
    const accessToken = await this.getTokenOrRefresh();
    if (!accessToken) {
      throw new Error('No access token available');
    }

    if (!this.appFolderId) {
      throw new Error('App folder not initialized');
    }

    const bibliotecaId = await this.findOrCreateSubfolder('Biblioteca');
    const highlightsId = await this.findOrCreateFolderInParent(bibliotecaId, 'Highlights');

    const fileName = `${workId}.json`;
    const query = `name='${fileName}' and '${highlightsId}' in parents and trashed=false`;
    const searchUrl = `${DRIVE_API_BASE}/files?q=${encodeURIComponent(query)}&fields=files(id)`;

    const searchResponse = await driveFetch(searchUrl, {
      headers: { 'Authorization': `Bearer ${accessToken}` }
    });

    if (!searchResponse.ok) {
      throw new Error(`Failed to search for ${fileName}: ${searchResponse.statusText}`);
    }

    const searchData = await searchResponse.json();
    const dataWithSchema = Array.isArray(data) ? data : stampSchema(data as SavedPdfData);
    validateBeforeSave(dataWithSchema, 'saveHighlights');
    const fileContent = JSON.stringify(dataWithSchema, null, 2);

    if (searchData.files && searchData.files.length > 0) {
      // Atualizar arquivo existente
      const fileId = searchData.files[0].id;

      // Backup .bak antes de sobrescrever (once per session per workId)
      await this.createBackupIfNeeded(fileId, highlightsId, fileName, accessToken);

      const updateUrl = `https://www.googleapis.com/upload/drive/v3/files/${fileId}?uploadType=media`;

      const updateResponse = await driveFetch(updateUrl, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: fileContent
      });

      if (!updateResponse.ok) {
        throw new Error(`Failed to update ${fileName}: ${updateResponse.statusText}`);
      }
    } else {
      // Criar novo arquivo
      const metadata = {
        name: fileName,
        mimeType: 'application/json',
        parents: [highlightsId]
      };

      const form = new FormData();
      form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
      form.append('file', new Blob([fileContent], { type: 'application/json' }));

      const uploadUrl = 'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart';
      const response = await driveFetch(uploadUrl, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${accessToken}` },
        body: form
      });

      if (!response.ok) {
        throw new Error(`Failed to create ${fileName}: ${response.statusText}`);
      }
    }
    auditLog({ operation: 'save', target: 'highlights', detail: `Highlights salvos para workId=${workId}`, success: true });
    } catch (error) {
      auditLog({ operation: 'save', target: 'highlights', detail: `Falha ao salvar highlights para workId=${workId}`, success: false, error: String(error) });
      throw error;
    }
  }

  /**
   * Carrega highlights de um PDF.
   *
   * @param workId ID do PDF/work no Drive
   * @param codebook (opcional, Sprint 0.2) — codebook empírico da Mesa para
   *   converter `empiricalTags` legacy (labels-string) para IDs do codebook (DN9).
   *   Backward-compatible: callers que não passam continuam funcionando como antes.
   *   Caller crítico do flanco empírico (`useHighlights.ts:157,224`) será atualizado
   *   no Sprint 0.3 para passar `activeProject?.empiricalCodebook` quando
   *   `empiricalMode === true`. Outros 19 callers do flanco teórico não mudam.
   */
  async loadHighlights(workId: string, codebook?: EmpiricalCodebook): Promise<SavedPdfData | null> {
    const accessToken = await this.getTokenOrRefresh();
    if (!accessToken) {
      throw new Error('No access token available');
    }

    if (!this.appFolderId) {
      throw new Error('App folder not initialized');
    }

    const bibliotecaId = await this.findOrCreateSubfolder('Biblioteca');
    const highlightsId = await this.findOrCreateFolderInParent(bibliotecaId, 'Highlights');

    const fileName = `${workId}.json`;
    const query = `name='${fileName}' and '${highlightsId}' in parents and trashed=false`;
    const searchUrl = `${DRIVE_API_BASE}/files?q=${encodeURIComponent(query)}&fields=files(id)`;

    const searchResponse = await driveFetch(searchUrl, {
      headers: { 'Authorization': `Bearer ${accessToken}` }
    });

    if (!searchResponse.ok) {
      throw new Error(`Failed to search for ${fileName}: ${searchResponse.statusText}`);
    }

    const searchData = await searchResponse.json();

    if (!searchData.files || searchData.files.length === 0) {
      return null;
    }

    const fileId = searchData.files[0].id;
    const downloadUrl = `${DRIVE_API_BASE}/files/${fileId}?alt=media`;

    const downloadResponse = await driveFetch(downloadUrl, {
      headers: { 'Authorization': `Bearer ${accessToken}` }
    });

    if (!downloadResponse.ok) {
      throw new Error(`Failed to download ${fileName}: ${downloadResponse.statusText}`);
    }

    const jsonText = await downloadResponse.text();
    try {
      const data: SavedPdfData = JSON.parse(jsonText);
      const result = migrateIfNeeded(data, 'highlights');
      if (result.migrated) {
        try {
          await this.saveHighlights(workId, result.data);
          logger.info('GoogleDriveRepository', `Migration save completed for highlights (workId: ${workId})`);
        } catch (migErr) {
          logger.error('GoogleDriveRepository', 'Migration save failed — will retry on next open', { workId, error: migErr });
        }
      }
      // Fase Empírica v4 Sprint 0.2 — se codebook fornecido, converte labels→IDs nos highlights
      // (Sprint 0.1 criou convertHighlightLabelsToIds; aqui é o wire opcional do M1)
      return applyCodebookConversion(result.data, codebook);
    } catch (err) {
      logger.error('GoogleDriveRepository', `JSON.parse failed for ${fileName} — trying .bak fallback:`, err);
      const fallback = await this.readBakFallback<SavedPdfData>(
        highlightsId, fileName, accessToken
      );
      if (fallback) {
        logger.info('GoogleDriveRepository', `Restored ${fileName} from .bak`);
        const bakResult = migrateIfNeeded(fallback, 'highlights');
        if (bakResult.migrated) {
          try {
            await this.saveHighlights(workId, bakResult.data);
            logger.info('GoogleDriveRepository', `Migration save completed (bak path, workId: ${workId})`);
          } catch (migErr) {
            logger.error('GoogleDriveRepository', 'Migration save failed (bak path) — will retry on next open', { workId, error: migErr });
          }
        }
        return applyCodebookConversion(bakResult.data, codebook);
      }
      throw new Error(`Invalid JSON in ${fileName} (no .bak available)`);
    }
  }

  /**
   * Deleta highlights de um PDF
   */
  async deleteHighlights(workId: string): Promise<void> {
    const accessToken = await this.getTokenOrRefresh();
    if (!accessToken) {
      throw new Error('No access token available');
    }

    if (!this.appFolderId) {
      throw new Error('App folder not initialized');
    }

    const bibliotecaId = await this.findOrCreateSubfolder('Biblioteca');
    const highlightsId = await this.findOrCreateFolderInParent(bibliotecaId, 'Highlights');

    const fileName = `${workId}.json`;
    const query = `name='${fileName}' and '${highlightsId}' in parents and trashed=false`;
    const searchUrl = `${DRIVE_API_BASE}/files?q=${encodeURIComponent(query)}&fields=files(id)`;

    const searchResponse = await driveFetch(searchUrl, {
      headers: { 'Authorization': `Bearer ${accessToken}` }
    });

    if (!searchResponse.ok) return;

    const searchData = await searchResponse.json();

    if (searchData.files && searchData.files.length > 0) {
      const fileId = searchData.files[0].id;
      await driveFetch(`${DRIVE_API_BASE}/files/${fileId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });
    }
  }

  // ==================== SCRATCHPAD ====================

  /**
   * Save scratchpad notes to Bigwa_App/Scratchpad/notes.json
   * Follows the same pattern as saveReferencesToDrive
   */
  async saveScratchpadNotes(notes: ScratchpadNote[]): Promise<void> {
    try {
    const accessToken = await this.getTokenOrRefresh();
    if (!accessToken) {
      throw new Error('No access token available');
    }

    if (!this.appFolderId) {
      const initialized = await this.ensureAppStructure();
      if (!initialized || !this.appFolderId) {
        throw new Error('App folder not initialized');
      }
    }

    // Get or create Scratchpad folder
    const scratchpadId = await this.findOrCreateSubfolder('Scratchpad');
    // Phase 2a: register folder ID
    const { registerFolderIdAsync } = await import('./driveFileHelper');
    registerFolderIdAsync('scratchpad', scratchpadId);

    const stamped = stampSchemaArray(notes);
    validateBeforeSave(stamped, 'saveScratchpadNotes');
    const fileContent = JSON.stringify(stamped, null, 2);

    // Phase 2a: try cached ID first
    const { findFileByIdOrName } = await import('./driveFileHelper');
    const { driveConfig } = await import('../driveConfig');
    const cachedFileId = driveConfig.getFileId('scratchpadNotesJson');

    const found = await findFileByIdOrName({
      cachedFileId,
      parentFolderId: scratchpadId,
      fileName: 'notes.json',
      accessToken,
      configFileKey: 'scratchpadNotesJson',
    });

    // Backup .bak antes de sobrescrever (once per session)
    if (found?.fileId) {
      await this.createBackupIfNeeded(found.fileId, scratchpadId, 'notes.json', accessToken);
    }

    if (found?.fromCache) {
      // Cached ID is canonical — PATCH directly
      const updateUrl = `https://www.googleapis.com/upload/drive/v3/files/${found.fileId}?uploadType=media`;
      const cachedEtag = etagCache.get(found.fileId);
      const updateResponse = await driveFetch(updateUrl, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          ...(cachedEtag ? { 'If-Match': cachedEtag } : {}),
        },
        body: fileContent
      });

      if (updateResponse.status === 412) {
        etagCache.delete(found.fileId);
        logger.error('GoogleDriveRepository', 'Conflito detectado em notes.json — arquivo modificado em outro dispositivo');
        throw new DriveConflictError('notes.json');
      }

      if (!updateResponse.ok) {
        throw new Error(`Failed to update notes.json: ${updateResponse.statusText}`);
      }
      const newEtag = updateResponse.headers.get('ETag');
      if (newEtag) etagCache.set(found.fileId, newEtag);
      auditLog({ operation: 'save', target: 'scratchpad', detail: `${notes.length} notas do scratchpad salvas no Drive (cache hit)`, success: true });
      return;
    }

    if (found && !found.fromCache) {
      // Found via name search — handle duplicates
      const files = found.files || [{ id: found.fileId }];

      if (files.length > 1) {
        logger.warn('GoogleDriveRepository', `Found ${files.length} notes.json files! Cleaning up...`);

        const sortedFiles = files.sort((a: { id: string; modifiedTime?: string }, b: { id: string; modifiedTime?: string }) => {
          const timeA = new Date(a.modifiedTime || 0).getTime();
          const timeB = new Date(b.modifiedTime || 0).getTime();
          return timeB - timeA;
        });

        const duplicatesToDelete = sortedFiles.slice(1);
        for (const duplicate of duplicatesToDelete) {
          try {
            await driveFetch(`${DRIVE_API_BASE}/files/${duplicate.id}`, {
              method: 'DELETE',
              headers: { 'Authorization': `Bearer ${accessToken}` }
            });
          } catch (error) {
            logger.error('GoogleDriveRepository', `Failed to delete duplicate ${duplicate.id}:`, error);
          }
        }
      }

      const fileId = files.length === 1 ? files[0].id : files.sort(
        (a: { id: string; modifiedTime?: string }, b: { id: string; modifiedTime?: string }) =>
          new Date(b.modifiedTime || 0).getTime() - new Date(a.modifiedTime || 0).getTime()
      )[0].id;

      const updateUrl = `https://www.googleapis.com/upload/drive/v3/files/${fileId}?uploadType=media`;
      const cachedEtag = etagCache.get(fileId);
      const updateResponse = await driveFetch(updateUrl, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
          ...(cachedEtag ? { 'If-Match': cachedEtag } : {}),
        },
        body: fileContent
      });

      if (updateResponse.status === 412) {
        etagCache.delete(fileId);
        logger.error('GoogleDriveRepository', 'Conflito detectado em notes.json — arquivo modificado em outro dispositivo');
        throw new DriveConflictError('notes.json');
      }

      if (!updateResponse.ok) {
        throw new Error(`Failed to update notes.json: ${updateResponse.statusText}`);
      }
      const newEtag = updateResponse.headers.get('ETag');
      if (newEtag) etagCache.set(fileId, newEtag);
      auditLog({ operation: 'save', target: 'scratchpad', detail: `${notes.length} notas do scratchpad salvas no Drive (name search)`, success: true });
      return;
    }

    // Not found anywhere — create new file
    const metadata = {
      name: 'notes.json',
      mimeType: 'application/json',
      parents: [scratchpadId]
    };

    const file = new Blob([fileContent], { type: 'application/json' });
    const form = new FormData();
    form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
    form.append('file', file);

    const uploadUrl = 'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id';
    const response = await driveFetch(uploadUrl, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${accessToken}` },
      body: form
    });

    if (!response.ok) {
      throw new Error(`Failed to create notes.json: ${response.statusText}`);
    }

    // Register newly created file ID
    const created = await response.json();
    driveConfig.registerFileId('scratchpadNotesJson', created.id).catch(() => {});
    auditLog({ operation: 'save', target: 'scratchpad', detail: `${notes.length} notas do scratchpad salvas no Drive`, success: true });
    } catch (error) {
      auditLog({ operation: 'save', target: 'scratchpad', detail: 'Falha ao salvar notas do scratchpad no Drive', success: false, error: String(error) });
      throw error;
    }
  }

  /**
   * Load scratchpad notes from Bigwa_App/Scratchpad/notes.json
   * Returns null if file doesn't exist
   */
  async loadScratchpadNotes(): Promise<ScratchpadNote[] | null> {
    const accessToken = await this.getTokenOrRefresh();
    if (!accessToken) {
      throw new Error('No access token available');
    }

    if (!this.appFolderId) {
      const initialized = await this.ensureAppStructure();
      if (!initialized || !this.appFolderId) {
        throw new Error('App folder not initialized');
      }
    }

    // Get Scratchpad folder
    const scratchpadId = await this.findOrCreateSubfolder('Scratchpad');
    // Phase 2a: register folder ID
    const { registerFolderIdAsync, readFileByIdOrName } = await import('./driveFileHelper');
    registerFolderIdAsync('scratchpad', scratchpadId);

    // Phase 2a: ID-first read
    const { driveConfig } = await import('../driveConfig');
    const cachedFileId = driveConfig.getFileId('scratchpadNotesJson');

    // If we have a cached ID, try direct read (skip duplicate check — cached ID is canonical)
    if (cachedFileId) {
      const result = await readFileByIdOrName({
        cachedFileId,
        parentFolderId: scratchpadId,
        fileName: 'notes.json',
        accessToken,
        configFileKey: 'scratchpadNotesJson',
      });

      if (!result) return null;

      try {
        const notes: ScratchpadNote[] = JSON.parse(result.content);
        return migrateArrayIfNeeded(notes, 'scratchpadNotes').data;
      } catch (err) {
        logger.error('GoogleDriveRepository', 'JSON.parse failed for notes.json — trying .bak fallback:', err);
        const fallback = await this.readBakFallback<ScratchpadNote[]>(
          scratchpadId, 'notes.json', accessToken
        );
        if (fallback) {
          logger.info('GoogleDriveRepository', 'Restored notes.json from .bak');
          return migrateArrayIfNeeded(fallback, 'scratchpadNotes').data;
        }
        throw new Error('Invalid JSON in notes.json (no .bak available)');
      }
    }

    // No cached ID — name search with duplicate handling
    const query = `name='notes.json' and '${scratchpadId}' in parents and trashed=false`;
    const searchUrl = `${DRIVE_API_BASE}/files?q=${encodeURIComponent(query)}&fields=files(id,modifiedTime)`;

    const searchResponse = await driveFetch(searchUrl, {
      headers: { 'Authorization': `Bearer ${accessToken}` }
    });

    if (!searchResponse.ok) {
      throw new Error(`Failed to search for notes.json: ${searchResponse.statusText}`);
    }

    const searchData = await searchResponse.json();

    if (!searchData.files || searchData.files.length === 0) {
      return null;
    }

    let fileId: string;
    if (searchData.files.length > 1) {
      logger.warn('GoogleDriveRepository', `Found ${searchData.files.length} notes.json files! Choosing most recent...`);

      const sortedFiles = searchData.files.sort((a: { id: string; modifiedTime?: string }, b: { id: string; modifiedTime?: string }) => {
        const timeA = new Date(a.modifiedTime || 0).getTime();
        const timeB = new Date(b.modifiedTime || 0).getTime();
        return timeB - timeA;
      });

      fileId = sortedFiles[0].id;

      const duplicatesToDelete = sortedFiles.slice(1);
      for (const duplicate of duplicatesToDelete) {
        try {
          await driveFetch(`${DRIVE_API_BASE}/files/${duplicate.id}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${accessToken}` }
          });
        } catch (error) {
          logger.error('GoogleDriveRepository', `Failed to delete duplicate ${duplicate.id}:`, error);
        }
      }
    } else {
      fileId = searchData.files[0].id;
    }

    // Register file ID
    driveConfig.registerFileId('scratchpadNotesJson', fileId).catch(() => {});

    const downloadUrl = `${DRIVE_API_BASE}/files/${fileId}?alt=media`;
    const downloadResponse = await driveFetch(downloadUrl, {
      headers: { 'Authorization': `Bearer ${accessToken}` }
    });

    if (!downloadResponse.ok) {
      throw new Error(`Failed to download notes.json: ${downloadResponse.statusText}`);
    }

    const jsonText = await downloadResponse.text();
    try {
      const notes: ScratchpadNote[] = JSON.parse(jsonText);
      return migrateArrayIfNeeded(notes, 'scratchpadNotes').data;
    } catch (err) {
      logger.error('GoogleDriveRepository', 'JSON.parse failed for notes.json — trying .bak fallback:', err);
      const fallback = await this.readBakFallback<ScratchpadNote[]>(
        scratchpadId, 'notes.json', accessToken
      );
      if (fallback) {
        logger.info('GoogleDriveRepository', 'Restored notes.json from .bak');
        return migrateArrayIfNeeded(fallback, 'scratchpadNotes').data;
      }
      throw new Error('Invalid JSON in notes.json (no .bak available)');
    }
  }

  // ============================================
  // NAMING CONFIG & FILE MANAGEMENT
  // ============================================

  /**
   * Carrega configuração de nomeação do Google Drive
   * @returns JSON string da configuração ou null se não existir
   */
  async loadNamingConfig(): Promise<string | null> {
    const accessToken = await this.getTokenOrRefresh();
    if (!accessToken) {
      logger.warn('GoogleDriveRepository', 'No access token for loadNamingConfig');
      return null;
    }

    try {
      // Garantir que temos a pasta Config (parentId primeiro, depois name)
      const configFolderId = await this.findOrCreateFolderInParent(this.appFolderId!, 'Config');
      // Phase 2a: register folder ID
      const { registerFolderIdAsync, readFileByIdOrName } = await import('./driveFileHelper');
      registerFolderIdAsync('config', configFolderId);

      // Phase 2a: ID-first read
      const { driveConfig } = await import('../driveConfig');
      const cachedFileId = driveConfig.getFileId('namingConfigJson');

      const result = await readFileByIdOrName({
        cachedFileId,
        parentFolderId: configFolderId,
        fileName: 'naming-config.json',
        accessToken,
        configFileKey: 'namingConfigJson',
      });

      if (!result) return null;
      try {
        const parsed = JSON.parse(result.content) as Record<string, unknown>;
        return JSON.stringify(migrateIfNeeded(parsed, 'namingConfig').data, null, 2);
      } catch {
        return result.content; // fail-safe: return as-is
      }
    } catch (error) {
      logger.error('GoogleDriveRepository', 'Erro ao carregar configuração de nomeação:', error);
      return null;
    }
  }

  /**
   * Salva configuração de nomeação no Google Drive
   * @param configJson JSON string da configuração
   */
  async saveNamingConfig(configJson: string): Promise<void> {
    const accessToken = await this.getTokenOrRefresh();
    if (!accessToken) {
      throw new Error('No access token available');
    }

    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(configJson) as Record<string, unknown>;
    } catch (err) {
      logger.error('GoogleDriveRepository', 'JSON.parse failed for naming config:', err);
      throw new Error('Invalid JSON in naming config');
    }
    const stamped = stampSchema(parsed);
    const stampedJson = JSON.stringify(stamped, null, 2);
    validateBeforeSave(stamped, 'saveNamingConfig');

    try {
      // Garantir que temos a pasta Config (parentId primeiro, depois name)
      const configFolderId = await this.findOrCreateFolderInParent(this.appFolderId!, 'Config');
      // Phase 2a: register folder ID
      const { registerFolderIdAsync, findFileByIdOrName } = await import('./driveFileHelper');
      registerFolderIdAsync('config', configFolderId);

      // Phase 2a: try cached ID first
      const { driveConfig } = await import('../driveConfig');
      const cachedFileId = driveConfig.getFileId('namingConfigJson');

      const found = await findFileByIdOrName({
        cachedFileId,
        parentFolderId: configFolderId,
        fileName: 'naming-config.json',
        accessToken,
        configFileKey: 'namingConfigJson',
      });

      // Backup .bak antes de sobrescrever (once per session)
      if (found?.fileId) {
        await this.createBackupIfNeeded(found.fileId, configFolderId, 'naming-config.json', accessToken);
      }

      if (found) {
        // Update existing file
        const updateUrl = `https://www.googleapis.com/upload/drive/v3/files/${found.fileId}?uploadType=media`;

        const updateResponse = await driveFetch(updateUrl, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: stampedJson,
        });

        if (!updateResponse.ok) {
          throw new Error(`Erro ao atualizar configuração: ${updateResponse.status}`);
        }
      } else {
        // Create new file
        const metadata = {
          name: 'naming-config.json',
          parents: [configFolderId],
          mimeType: 'application/json',
        };

        const form = new FormData();
        form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
        form.append('file', new Blob([stampedJson], { type: 'application/json' }));

        const createUrl = 'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id';

        const createResponse = await driveFetch(createUrl, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${accessToken}` },
          body: form,
        });

        if (!createResponse.ok) {
          throw new Error(`Erro ao criar configuração: ${createResponse.status}`);
        }

        // Register newly created file ID
        const created = await createResponse.json();
        driveConfig.registerFileId('namingConfigJson', created.id).catch(() => {});
      }
      auditLog({ operation: 'save', target: 'configuração nomeação', detail: 'Configuração de nomeação salva no Drive', success: true });
    } catch (error) {
      auditLog({ operation: 'save', target: 'configuração nomeação', detail: 'Falha ao salvar configuração de nomeação no Drive', success: false, error: String(error) });
      logger.error('GoogleDriveRepository', 'Erro ao salvar configuração de nomeação:', error);
      throw error;
    }
  }

  // ============================================
  // SINTESES (per-project)
  // ============================================

  /**
   * Carrega sínteses de um projeto do Google Drive
   * @param projectId ID do projeto (Drive folder ID)
   * @returns JSON string das sínteses ou null se não existir
   */
  async loadSinteses(projectId: string): Promise<string | null> {
    const accessToken = await this.getTokenOrRefresh();
    if (!accessToken) {
      logger.warn('GoogleDriveRepository', 'No access token for loadSinteses');
      return null;
    }

    try {
      // Search by name within the project folder (no configFileKey — per-project file)
      const query = `name='sinteses.json' and '${projectId}' in parents and trashed=false`;
      const searchUrl = `${DRIVE_API_BASE}/files?q=${encodeURIComponent(query)}&fields=files(id)`;

      const searchResponse = await driveFetch(searchUrl, {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });

      if (!searchResponse.ok) {
        throw new Error(`Failed to search for sinteses.json: ${searchResponse.statusText}`);
      }

      const searchData = await searchResponse.json();
      if (!searchData.files || searchData.files.length === 0) {
        return null;
      }

      const fileId = searchData.files[0].id;
      const downloadUrl = `${DRIVE_API_BASE}/files/${fileId}?alt=media`;
      const downloadResponse = await driveFetch(downloadUrl, {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });

      if (!downloadResponse.ok) {
        throw new Error(`Failed to download sinteses.json: ${downloadResponse.statusText}`);
      }

      const content = await downloadResponse.text();
      try {
        const parsed = JSON.parse(content) as Record<string, unknown>;
        return JSON.stringify(migrateIfNeeded(parsed, 'sinteses').data, null, 2);
      } catch {
        return content; // fail-safe: return as-is
      }
    } catch (error) {
      logger.error('GoogleDriveRepository', 'Erro ao carregar sínteses:', error);
      return null;
    }
  }

  /**
   * Salva sínteses de um projeto no Google Drive
   * @param projectId ID do projeto (Drive folder ID)
   * @param sintesesJson JSON string das sínteses
   */
  async saveSinteses(projectId: string, sintesesJson: string): Promise<void> {
    return withSinteseSaveLock(async () => {
      const accessToken = await this.getTokenOrRefresh();
      if (!accessToken) {
        throw new Error('No access token available');
      }

      let parsed: Record<string, unknown>;
      try {
        parsed = JSON.parse(sintesesJson) as Record<string, unknown>;
      } catch (err) {
        logger.error('GoogleDriveRepository', 'JSON.parse failed for sinteses:', err);
        throw new Error('Invalid JSON in sinteses');
      }
      const stamped = stampSchema(parsed);
      const stampedJson = JSON.stringify(stamped, null, 2);
      validateBeforeSave(stamped, 'saveSinteses');

      try {
        // Search by name within the project folder (no configFileKey — per-project file)
        const query = `name='sinteses.json' and '${projectId}' in parents and trashed=false`;
        const searchUrl = `${DRIVE_API_BASE}/files?q=${encodeURIComponent(query)}&fields=files(id)`;

        const searchResponse = await driveFetch(searchUrl, {
          headers: { 'Authorization': `Bearer ${accessToken}` }
        });

        if (!searchResponse.ok) {
          throw new Error(`Failed to search for sinteses.json: ${searchResponse.statusText}`);
        }

        const searchData = await searchResponse.json();

        if (searchData.files && searchData.files.length > 0) {
          // Update existing file
          const fileId = searchData.files[0].id;
          const updateUrl = `https://www.googleapis.com/upload/drive/v3/files/${fileId}?uploadType=media`;

          const updateResponse = await driveFetch(updateUrl, {
            method: 'PATCH',
            headers: {
              'Authorization': `Bearer ${accessToken}`,
              'Content-Type': 'application/json',
            },
            body: stampedJson,
          });

          if (!updateResponse.ok) {
            throw new Error(`Erro ao atualizar sínteses: ${updateResponse.status}`);
          }
        } else {
          // Create new file
          const metadata = {
            name: 'sinteses.json',
            parents: [projectId],
            mimeType: 'application/json',
          };

          const form = new FormData();
          form.append('metadata', new Blob([JSON.stringify(metadata)], { type: 'application/json' }));
          form.append('file', new Blob([stampedJson], { type: 'application/json' }));

          const createUrl = 'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id';

          const createResponse = await driveFetch(createUrl, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${accessToken}` },
            body: form,
          });

          if (!createResponse.ok) {
            throw new Error(`Erro ao criar sínteses: ${createResponse.status}`);
          }
        }
        auditLog({ operation: 'save', target: 'sínteses', targetId: projectId, detail: 'Sínteses salvas no Drive', success: true });
      } catch (error) {
        auditLog({ operation: 'save', target: 'sínteses', targetId: projectId, detail: 'Falha ao salvar sínteses no Drive', success: false, error: String(error) });
        logger.error('GoogleDriveRepository', 'Erro ao salvar sínteses:', error);
        throw error;
      }
    });
  }

  /**
   * Renomeia um arquivo no Google Drive
   * @param fileId ID do arquivo a renomear
   * @param newName Novo nome do arquivo
   */
  async renameFile(fileId: string, newName: string): Promise<boolean> {
    const accessToken = await this.getTokenOrRefresh();
    if (!accessToken) {
      logger.warn('GoogleDriveRepository', 'Token nao encontrado para renomear');
      return false;
    }

    try {
      const response = await driveFetch(
        `${DRIVE_API_BASE}/files/${fileId}`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ name: newName }),
        }
      );

      if (!response.ok) {
        const error = await response.json().catch(function() { return {}; });
        logger.error('GoogleDriveRepository', 'Erro ao renomear:', error);
        return false;
      }

      return true;
    } catch (error) {
      logger.error('GoogleDriveRepository', 'Falha ao renomear:', error);
      return false;
    }
  }

  /**
   * Lista nomes de arquivos em uma pasta (para verificar duplicatas)
   * @param folderId ID da pasta
   * @returns Lista de nomes de arquivos
   */
  async listFilenames(folderId: string): Promise<string[]> {
    const accessToken = await this.getTokenOrRefresh();
    if (!accessToken) {
      return [];
    }

    try {
      const query = `'${folderId}' in parents and trashed=false`;
      const url = `${DRIVE_API_BASE}/files?q=${encodeURIComponent(query)}&fields=files(name)&pageSize=1000`;

      const response = await driveFetch(url, {
        headers: { 'Authorization': `Bearer ${accessToken}` }
      });

      if (!response.ok) {
        throw new Error(`Erro ao listar arquivos: ${response.status}`);
      }

      const data = await response.json();
      return data.files?.map((f: { name: string }) => f.name) || [];
    } catch (error) {
      logger.error('GoogleDriveRepository', 'Erro ao listar arquivos:', error);
      return [];
    }
  }
}
